import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PermissionProvider } from './context/PermissionContext';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import Dashboard from './pages/Dashboard';
import Courses from './pages/Courses';
import AddCourse from './pages/AddCourse';
import AddContent from './pages/AddContent';
import ViewContent from './pages/ViewContent';
import ImportContents from './pages/ImportContents';
import CourseCategories from './pages/CourseCategories';
import AllowCourses from './pages/AllowCourses';
import LiveVideos from './pages/LiveVideos';
import Teachers from './pages/Teachers';
import Students from './pages/Students';

import AddTeacher from './pages/AddTeacher';
import Exams from './pages/Exams';
import QuestionBank from './pages/QuestionBank';
import AddQuestion from './pages/AddQuestion';
import ExamQuestions from './pages/ExamQuestions';
import AIExamGenerator from './pages/AIExamGenerator';
import Settings from './pages/Settings';
import BrandingSettings from './pages/BrandingSettings';
import RolePermissions from './pages/RolePermissions';
import Blogs from './pages/Blogs';
import BlogForm from './pages/BlogForm';
import LegalPages from './pages/LegalPages';
import LegalPageForm from './pages/LegalPageForm';
import Masters from './pages/Masters';
import CoursePreferences from './pages/CoursePreferences';
import Classes from './pages/Classes';
import Batches from './pages/Batches';
import Attendance from './pages/Attendance';
import AttendanceReports from './pages/AttendanceReports';
import AttendanceScanner from './pages/AttendanceScanner';
import BulkIDCards from './pages/BulkIDCards';
import AddStudent from './pages/AddStudent';
import Holidays from './pages/Holidays';
import StaffIDCards from './pages/StaffIDCards';
import StaffAttendanceScanner from './pages/StaffAttendanceScanner';
import StaffAttendanceReports from './pages/StaffAttendanceReports';
import FailedPayments from './pages/FailedPayments';
import Transactions from './pages/Transactions';
import PaymentHistory from './pages/PaymentHistory';
import FeeMaster from './pages/FeeMaster';
import FeeGroup from './pages/FeeGroup';
import FeeType from './pages/FeeType';
import FeeDiscount from './pages/FeeDiscount';
import FeeReminders from './pages/FeeReminders';
import StaffAttendance from './pages/StaffAttendance';
import Payroll from './pages/Payroll';
import PayrollCreate from './pages/PayrollCreate';
import LeaveRequest from './pages/LeaveRequest';
import Layout from './components/layout/Layout';
import Timetable from './pages/Timetable';
import Assignments from './pages/Assignments';
import AssignmentForm from './pages/AssignmentForm';
import AssignmentSubmissions from './pages/AssignmentSubmissions';
import OfflineExams from './pages/OfflineExams';
import MarksEntry from './pages/MarksEntry';
import GenerateReportCard from './pages/GenerateReportCard';
import ViewReportCards from './pages/ViewReportCards';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return !isAuthenticated ? children : <Navigate to="/dashboard" />;
};

function AppRoutes() {
  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />

        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="courses/list" element={<Courses />} />
          <Route path="courses/add" element={<AddCourse />} />
          <Route path="courses/:id/edit" element={<AddCourse />} />
          <Route path="courses/:courseId/addcontent" element={<AddContent />} />
          <Route path="courses/:courseId/viewcontent" element={<ViewContent />} />
          <Route path="courses/:courseId/import" element={<ImportContents />} />
          <Route path="courses/categories" element={<CourseCategories />} />
          <Route path="courses/allow" element={<AllowCourses />} />
          <Route path="courses/live-videos" element={<LiveVideos />} />
          <Route path="teachers" element={<Teachers />} />
          <Route path="teachers/create" element={<AddTeacher />} />
          <Route path="teachers/:id/edit" element={<AddTeacher />} />
          <Route path="exams" element={<Exams />} />
          <Route path="exams/:examId/questions" element={<ExamQuestions />} />
          <Route path="questions" element={<QuestionBank />} />
          <Route path="questions/add" element={<AddQuestion />} />
          <Route path="questions/edit/:id" element={<AddQuestion />} />
          <Route path="questions/view/:id" element={<AddQuestion />} />
          <Route path="ai-exam-generator" element={<AIExamGenerator />} />
          <Route path="masters/tradegroups" element={<Masters tab="tradegroups" />} />
          <Route path="masters/trades" element={<Masters tab="trades" />} />
          <Route path="masters/coursetypes" element={<Masters tab="coursetypes" />} />
          <Route path="masters/subjects" element={<Masters tab="subjects" />} />
          <Route path="masters/chapters" element={<Masters tab="chapters" />} />
          <Route path="masters/topics" element={<Masters tab="topics" />} />
          <Route path="masters/preferences" element={<CoursePreferences />} />
          <Route path="masters/classes" element={<Classes />} />
          <Route path="masters/batches" element={<Batches />} />
          <Route path="attendance/mark" element={<Attendance />} />
          <Route path="attendance/scanner" element={<AttendanceScanner />} />
          <Route path="attendance/reports" element={<AttendanceReports />} />
          <Route path="students/id-cards" element={<BulkIDCards />} />
          <Route path="holidays" element={<Holidays />} />
          <Route path="staff/id-cards" element={<StaffIDCards />} />
          <Route path="staff/attendance/scanner" element={<StaffAttendanceScanner />} />
          <Route path="staff/attendance/reports" element={<StaffAttendanceReports />} />
          <Route path="blogs" element={<Blogs />} />
          <Route path="blogs/create" element={<BlogForm />} />
          <Route path="blogs/edit/:id" element={<BlogForm />} />
          <Route path="legal-pages" element={<LegalPages />} />
          <Route path="legal-pages/create" element={<LegalPageForm />} />
          <Route path="legal-pages/edit/:id" element={<LegalPageForm />} />
          <Route path="settings" element={<Settings />} />
          <Route path="settings/branding" element={<BrandingSettings />} />
          <Route path="settings/permissions" element={<RolePermissions />} />
          <Route path="payments/transactions" element={<Transactions />} />
          <Route path="payments/history" element={<PaymentHistory />} />
          <Route path="payments/failed" element={<FailedPayments />} />
          <Route path="students" element={<Students />} />
          <Route path="students/add" element={<AddStudent />} />
          <Route path="students/edit/:id" element={<AddStudent />} />
          <Route path="fee-collection/master" element={<FeeMaster />} />
          <Route path="fee-collection/group" element={<FeeGroup />} />
          <Route path="fee-collection/type" element={<FeeType />} />
          <Route path="fee-collection/discount" element={<FeeDiscount />} />
          <Route path="fee-collection/reminders" element={<FeeReminders />} />
          <Route path="hrms/attendance" element={<StaffAttendance />} />
          <Route path="hrms/payroll" element={<Payroll />} />
          <Route path="hrms/payroll/create/:month/:year/:staffId" element={<PayrollCreate />} />
          <Route path="hrms/leave-request" element={<LeaveRequest />} />
          <Route path="timetable" element={<Timetable />} />
          <Route path="assignments" element={<Assignments />} />
          <Route path="assignments/create" element={<AssignmentForm />} />
          <Route path="assignments/:id/submissions" element={<AssignmentSubmissions />} />
          <Route path="assignments/edit/:id" element={<AssignmentForm />} />
          <Route path="report-cards/exams" element={<OfflineExams />} />
          <Route path="report-cards/marks" element={<MarksEntry />} />
          <Route path="report-cards/generate" element={<GenerateReportCard />} />
          <Route path="report-cards/view" element={<ViewReportCards />} />
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/dashboard" />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <PermissionProvider>
        <AppRoutes />
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </PermissionProvider>
    </AuthProvider>
  );
}

export default App;
